import React, { useState } from "react";
import { GoogleLogin } from '@react-oauth/google';
import { MessageComponent } from "@syncfusion/ej2-react-notifications";
import { API_PATHS } from "../config/apiConstants";

const SignUpModal = ({ isOpen, onClose, setIsAuthenticated, navigate }) => {
  const [signupCode, setSignupCode] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({ signupCode: "", phone: "" });
  const [globalError, setGlobalError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [googleCredential, setGoogleCredential] = useState(null);

  if (!isOpen) return null;

  const validatePhone = (phoneNumber) => {
    // Remove any spaces or dashes
    const cleanPhone = phoneNumber.replace(/[\s-]/g, '');
    
    // Check if it starts with 04 and has exactly 10 digits
    const australianMobileRegex = /^04\d{8}$/;
    return australianMobileRegex.test(cleanPhone);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    const numbersOnly = value.replace(/[^\d]/g, '');
    setPhone(numbersOnly);
    
    if (numbersOnly && errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleSignupCodeChange = (e) => {
    const value = e.target.value;
    setSignupCode(value);
    
    if (value && errors.signupCode) {
      setErrors((prev) => ({ ...prev, signupCode: "" }));
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleCredential(credentialResponse);
    await handleSubmit(credentialResponse);
  };

  const handleGoogleError = () => {
    setGlobalError("Google authentication failed. Please try again.");
  };

  const handleSubmit = async (credentialResponse) => {
    // Validate inputs
    const newErrors = {};
    
    if (!signupCode.trim()) {
      newErrors.signupCode = "Validation code is required";
    }
    
    if (phone && !validatePhone(phone)) {
      newErrors.phone = "Please enter a valid Australian mobile number (e.g., 0412345678)";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!credentialResponse) {
      setGlobalError("Please sign in with Google first");
      return;
    }

    setIsLoading(true);
    setGlobalError("");

    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_URL}${API_PATHS.CREATE_USER_WITH_GOOGLE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          googleToken: credentialResponse.credential,
          signupCode: signupCode.trim(),
          phone: phone || undefined, // Only include if provided
        }),
      });

      // Check if response has content before parsing
      const contentType = response.headers.get("content-type");
      let data = null;
      
      if (contentType && contentType.includes("application/json")) {
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            console.error("JSON parse error:", parseError);
            console.error("Response text:", text);
            throw new Error('Invalid response from server');
          }
        }
      }

      if (!response.ok) {
        const errorMessage = data?.message || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
      }

      if (data && data.success && data.user?.token) {
        // Store the JWT token
        localStorage.setItem("jwtToken", data.user.token);
        setIsAuthenticated(true);
        navigate("/overview");
      } else {
        throw new Error(data?.message || 'Invalid response from server');
      }
    } catch (error) {
      console.error("Sign up error:", error);
      setGlobalError(error.message || "Failed to create account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md mx-4 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
          disabled={isLoading}
        >
          &times;
        </button>

        {/* Header */}
        <h2 className="text-2xl font-bold mb-2 text-center text-primary">
          Create Account
        </h2>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Enter your details to get started
        </p>

        {/* Error Message */}
        {globalError && (
          <div className="mb-4">
            <MessageComponent
              severity="Error"
              content={globalError}
              showCloseIcon={true}
              onClose={() => setGlobalError("")}
            />
          </div>
        )}

        {/* Form */}
        <div className="space-y-4">
          {/* Validation Code Input */}
          <div>
            <label
              htmlFor="signupCode"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Validation Code <span className="text-red-500">*</span>
            </label>
            <input
              id="signupCode"
              type="text"
              placeholder="Enter validation code"
              value={signupCode}
              onChange={handleSignupCodeChange}
              className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none ${
                errors.signupCode
                  ? "border-red-500"
                  : "border-gray-300 focus:border-primary"
              }`}
              disabled={isLoading}
            />
            {errors.signupCode && (
              <p className="text-red-500 text-xs mt-1">{errors.signupCode}</p>
            )}
          </div>

          {/* Phone Number Input */}
          <div>
            <label
              htmlFor="phone"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Phone Number <span className="text-gray-400 text-xs">(Optional)</span>
            </label>
            <input
              id="phone"
              type="tel"
              placeholder="0412345678"
              value={phone}
              onChange={handlePhoneChange}
              maxLength={10}
              className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none ${
                errors.phone
                  ? "border-red-500"
                  : "border-gray-300 focus:border-primary"
              }`}
              disabled={isLoading}
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Australian mobile number (must start with 04)
            </p>
          </div>

          {/* Google Sign Up Button */}
          <div className="pt-4">
            <p className="text-sm text-gray-600 mb-3 text-center">
              Sign up with Google
            </p>
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                disabled={isLoading}
                theme="outline"
                size="large"
                text="signup_with"
                shape="rectangular"
              />
            </div>
          </div>
        </div>

        {/* Loading Indicator */}
        {isLoading && (
          <div className="mt-4 flex justify-center">
            <div className="animate-spin rounded-full border-t-2 border-l-2 border-primary h-8 w-8"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignUpModal;

